// Mapping between MusicXML <harmony> kinds and the app's chord catalogue.
// Legacy imports promote triads to four-part representatives (SPEC §10),
// while authored triads retain their exact type through native score exports.

import type { ChordTypeId } from '@/music/chords';

/** A standard document-level miscellaneous-field distinguishes native exports. */
export const MUSICXML_TRIAD_KINDS_FIELD = 'scoreguard-triad-kinds';

/** Literal triad kinds, used only when a native score requests their preservation. */
export function triadTypeFromKind(kind: string): ChordTypeId | undefined {
  switch (kind.trim().toLowerCase()) {
    case 'major': return 'maj';
    case 'minor': return 'min';
    case 'augmented': return 'aug';
    case 'diminished': return 'dim';
    default: return undefined;
  }
}

/**
 * MusicXML kind text -> catalogue chord type. Every sounding value of the
 * standard kind vocabulary has its own case, reduced to the nearest four-part
 * type the way the iReal parser's mapQuality reduces the same qualities
 * (m11/m13 → min9, ∆13 → maj9, 11 → dom9, any sus → 7sus4, a chord with no
 * third or seventh → the bare-triad maj7; the augmented sixths and Tristan
 * read as the seventh chords they sound as). Without them a minor 11th fell
 * to the dom7 default and played with a major third; that default is now
 * reached only by `other` and by text outside the standard.
 */
export function kindToChordType(kind: string): ChordTypeId {
  switch (kind.trim().toLowerCase()) {
    case 'major':
    case 'major-seventh':
    case 'maj7':
    case 'neapolitan':
    case 'power':
    case 'pedal':
      return 'maj7';
    case 'major-sixth':
    case '6':
      return 'maj6';
    case 'major-ninth':
    case 'major-11th':
    case 'major-13th':
      return 'maj9';
    case 'minor':
    case 'minor-seventh':
    case 'min7':
      return 'min7';
    case 'minor-sixth':
      return 'min6';
    case 'minor-ninth':
    case 'minor-11th':
    case 'minor-13th':
      return 'min9';
    case 'minor-major':
    case 'major-minor':
    case 'minmaj7':
      return 'minmaj7';
    case 'dominant':
    case 'dominant-seventh':
    case '7':
    case 'german':
    case 'italian':
      return 'dom7';
    case 'dominant-seventh-flat-five':
    case 'french':
      return 'dom7b5';
    case 'dominant-seventh-sharp-five':
      return 'dom7s5';
    case 'dominant-ninth':
    case 'dominant-11th':
      return 'dom9';
    case 'dominant-13th':
    case 'dominant-thirteenth':
      return 'dom13';
    case 'suspended-fourth':
    case 'suspended-second':
    case 'dominant-suspended':
      return 'dom7sus4';
    case 'half-diminished':
    case 'tristan':
      return 'min7b5';
    case 'diminished':
    case 'diminished-seventh':
      return 'dim7';
    case 'augmented':
    case 'augmented-seventh':
      return 'dom7s5';
    default:
      return 'dom7';
  }
}

/** Catalogue chord type -> a MusicXML kind for export. */
export function chordTypeToKind(type: ChordTypeId): string {
  const map: Partial<Record<ChordTypeId, string>> = {
    maj: 'major',
    min: 'minor',
    aug: 'augmented',
    dim: 'diminished',
    maj7: 'major-seventh',
    maj6: 'major-sixth',
    maj9: 'major-ninth',
    min7: 'minor-seventh',
    min6: 'minor-sixth',
    min9: 'minor-ninth',
    minmaj7: 'major-minor',
    dom7: 'dominant',
    dom9: 'dominant-ninth',
    dom13: 'dominant-13th',
    dom7sus4: 'suspended-fourth',
    min7b5: 'half-diminished',
    dim7: 'diminished-seventh',
    dom7s5: 'augmented-seventh',
  };
  return map[type] ?? 'dominant';
}

export interface ChordDegree {
  value: number;
  alter: number;
  type: 'add' | 'alter' | 'subtract';
}

// These catalogue colours have no MusicXML kind of their own. Express the
// parent chord and its degrees so other score readers can play them too.
const EXTENSIONS: Partial<Record<ChordTypeId, { base: ChordTypeId; degree: ChordDegree }>> = {
  maj7s5: { base: 'maj7', degree: { value: 5, alter: 1, type: 'alter' } },
  maj7b5: { base: 'maj7', degree: { value: 5, alter: -1, type: 'alter' } },
  maj69: { base: 'maj6', degree: { value: 9, alter: 0, type: 'add' } },
  dom7b5: { base: 'dom7', degree: { value: 5, alter: -1, type: 'alter' } },
  dom7b9: { base: 'dom7', degree: { value: 9, alter: -1, type: 'add' } },
  dom7s9: { base: 'dom7', degree: { value: 9, alter: 1, type: 'add' } },
  min7s5: { base: 'min7', degree: { value: 5, alter: 1, type: 'alter' } },
  min7b5b9: { base: 'min7b5', degree: { value: 9, alter: -1, type: 'add' } },
  min7b59: { base: 'min7b5', degree: { value: 9, alter: 0, type: 'add' } },
  min69: { base: 'min6', degree: { value: 9, alter: 0, type: 'add' } },
  dimmaj7: { base: 'minmaj7', degree: { value: 5, alter: -1, type: 'alter' } },
};

export function chordTypeToHarmony(type: ChordTypeId): { kind: string; degrees: ChordDegree[] } {
  const extension = EXTENSIONS[type];
  return {
    kind: chordTypeToKind(extension?.base ?? type),
    degrees: extension ? [extension.degree] : [],
  };
}

/** Apply the fifth/ninth degrees represented by the catalogue, in pitch order. */
export function applyChordDegrees(type: ChordTypeId, degrees: ChordDegree[]): ChordTypeId {
  let result = type;
  for (const degree of [...degrees].sort((a, b) => a.value - b.value)) {
    if (degree.type === 'subtract') continue;
    if (degree.value === 5 && degree.type === 'alter') {
      const alteredFifths: Partial<Record<ChordTypeId, [ChordTypeId, ChordTypeId]>> = {
        dom7: ['dom7b5', 'dom7s5'],
        maj7: ['maj7b5', 'maj7s5'],
        min7: ['min7b5', 'min7s5'],
      };
      const variants = alteredFifths[result];
      if (variants && Math.abs(degree.alter) === 1) {
        result = variants[degree.alter === -1 ? 0 : 1];
      } else if (result === 'minmaj7' && degree.alter === -1) {
        result = 'dimmaj7';
      }
    } else if (degree.value === 9) {
      if (result === 'dom7' || result === 'dom9' || result === 'dom13') {
        if (degree.alter === -1) result = 'dom7b9';
        if (degree.alter === 1) result = 'dom7s9';
        if (degree.alter === 0 && result === 'dom7') result = 'dom9';
      } else if (result === 'min7b5') {
        if (degree.alter === -1) result = 'min7b5b9';
        if (degree.alter === 0) result = 'min7b59';
      } else if (degree.alter === 0) {
        const ninths: Partial<Record<ChordTypeId, ChordTypeId>> = {
          maj7: 'maj9',
          maj6: 'maj69',
          min7: 'min9',
          min6: 'min69',
        };
        result = ninths[result] ?? result;
      }
    }
  }
  return result;
}
